import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { Button, Input, Tag, Textarea } from "../../../../design-system";
import {
  AREA_LABELS,
  BOARD_AREAS,
  BOARD_CARD_TYPES,
  BOARD_PRIORITIES,
  BoardArea,
  BoardCard,
  BoardCardType,
  BoardComment,
  BoardPriority,
  BoardStageDef,
} from "../../../../types/Board";
import {
  addBoardComment,
  deleteBoardCard,
  listenToBoardComments,
  updateBoardCard,
  uploadBoardAttachment,
} from "../../../../database/board/boardApi";
import { humanizeStage } from "../../outreach/components/leadUtils";
import { TYPE_LABELS, TYPE_VARIANT } from "./IssueBoard";
import {
  ActionsRow,
  Backdrop,
  CloseBtn,
  ErrorText,
  FieldLabel,
  ModalBody,
  ModalCard,
  ModalFoot,
  ModalHead,
  ModalSub,
  ModalTitle,
  Select,
} from "./modalKit";

// Card detail: full fields, comment thread, and screenshot attachments.
// Everyone can comment and attach; editing fields and deleting are gated by
// canEdit (board operators only).

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
`;

const Description = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  white-space: pre-wrap;
  margin: 0;
`;

const AttachmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: var(--space-2);
`;

const FileThumb = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: var(--space-2);
  font-size: 11px;
  text-align: center;
  word-break: break-word;
  color: var(--color-text-secondary);
`;

const Thumb = styled.a`
  display: block;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  aspect-ratio: 1;
  background: var(--color-surface-inset);

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover {
    border-color: var(--color-accent);
  }
`;

const DropHint = styled.button`
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  padding: var(--space-3);
  cursor: pointer;
  width: 100%;

  &:hover {
    border-color: var(--color-accent);
    color: var(--color-text-secondary);
  }
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const CommentEl = styled.div`
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
`;

const CommentHead = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-bottom: 2px;
`;

const CommentText = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  white-space: pre-wrap;
`;

const CommentRow = styled.div`
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
`;

type Author = { uid: string; fullName: string; communityId: string };

const CardDetailModal: React.FC<{
  card: BoardCard;
  stages: BoardStageDef[];
  canEdit: boolean;
  author: Author;
  onClose: () => void;
}> = ({ card, stages, canEdit, author, onClose }) => {
  const [comments, setComments] = useState<BoardComment[]>([]);
  const [commentText, setCommentText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description);
  const [type, setType] = useState<BoardCardType>(card.type);
  const [area, setArea] = useState<BoardArea>(card.area);
  const [priority, setPriority] = useState<BoardPriority>(card.priority);
  const [status, setStatus] = useState(card.status);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => listenToBoardComments(card.id, setComments), [card.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const stageLabel = (key: string) =>
    stages.find((s) => s.key === key)?.label || humanizeStage(key);

  const upload = async (files: FileList | File[]) => {
    const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!images.length) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of images) await uploadBoardAttachment(card.id, file);
    } catch (e: any) {
      setError(e?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const sendComment = async () => {
    if (!commentText.trim()) return;
    setSending(true);
    setError(null);
    try {
      await addBoardComment(card.id, commentText, author);
      setCommentText("");
    } catch (e: any) {
      setError(e?.message || "Could not add the comment.");
    } finally {
      setSending(false);
    }
  };

  const saveEdits = async () => {
    setError(null);
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    try {
      await updateBoardCard(card.id, {
        title,
        description,
        type,
        area,
        priority,
        status,
      });
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await deleteBoardCard(card.id);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Could not delete.");
      setSaving(false);
    }
  };

  return (
    <Backdrop onClick={onClose}>
      <ModalCard
        onClick={(e) => e.stopPropagation()}
        onPaste={(e) => {
          if (e.clipboardData?.files?.length) {
            e.preventDefault();
            upload(e.clipboardData.files);
          }
        }}
      >
        <ModalHead>
          <div style={{ minWidth: 0 }}>
            {editing ? (
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
              />
            ) : (
              <ModalTitle>{card.title}</ModalTitle>
            )}
            <ModalSub>
              {card.creatorCommunityName || card.creatorCommunityId} ·{" "}
              {card.source === "coach"
                ? `sent by ${
                    card.submittedBy?.name ||
                    card.submittedBy?.email ||
                    "the coach"
                  }`
                : card.createdBy.fullName}
              {card.createdAt && ` · ${moment(card.createdAt).fromNow()}`}
              {card.source === "coach" &&
                (card.reviewedAt
                  ? ` · reviewed ${moment(card.reviewedAt).fromNow()}`
                  : " · not reviewed yet")}
            </ModalSub>
          </div>
          <CloseBtn aria-label="Close" onClick={onClose}>
            ×
          </CloseBtn>
        </ModalHead>

        <ModalBody>
          {editing ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "var(--space-3)",
              }}
            >
              <div>
                <FieldLabel>Type</FieldLabel>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value as BoardCardType)}
                >
                  {BOARD_CARD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {TYPE_LABELS[t]}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <FieldLabel>Area</FieldLabel>
                <Select
                  value={area}
                  onChange={(e) => setArea(e.target.value as BoardArea)}
                >
                  {BOARD_AREAS.map((a) => (
                    <option key={a} value={a}>
                      {AREA_LABELS[a]}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <FieldLabel>Priority</FieldLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as BoardPriority)}
                >
                  {BOARD_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <FieldLabel>State</FieldLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {stages.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                  {!stages.some((s) => s.key === status) && (
                    <option value={status}>{stageLabel(status)}</option>
                  )}
                </Select>
              </div>
            </div>
          ) : (
            <MetaRow>
              <Tag variant={TYPE_VARIANT[card.type]} size="sm">
                {TYPE_LABELS[card.type]}
              </Tag>
              <Tag variant="neutral" size="sm">
                {AREA_LABELS[card.area]}
              </Tag>
              <Tag variant="neutral" size="sm">
                {stageLabel(card.status)}
              </Tag>
              <Tag variant="neutral" size="sm">
                {card.priority}
              </Tag>
            </MetaRow>
          )}

          <div>
            <FieldLabel>Description</FieldLabel>
            {editing ? (
              <Textarea
                value={description}
                rows={5}
                onChange={(e) => setDescription(e.target.value)}
              />
            ) : (
              <Description>{card.description || "—"}</Description>
            )}
          </div>

          <div>
            <FieldLabel>Attachments</FieldLabel>
            {card.attachments.length > 0 && (
              <AttachmentGrid style={{ marginBottom: "var(--space-2)" }}>
                {card.attachments.map((a) => {
                  // Coaches attach screen recordings through the MCP upload
                  // link, so an <img> is no longer a safe assumption.
                  const kind =
                    a.kind ||
                    (a.contentType?.startsWith("video/") ? "video" : "image");
                  return (
                    <Thumb
                      key={a.id}
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={a.name}
                    >
                      {kind === "video" ? (
                        <video src={a.url} muted playsInline preload="metadata" />
                      ) : kind === "image" ? (
                        <img src={a.url} alt={a.name} />
                      ) : (
                        <FileThumb>{a.name}</FileThumb>
                      )}
                    </Thumb>
                  );
                })}
              </AttachmentGrid>
            )}
            <DropHint
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading
                ? "Uploading…"
                : "Paste a screenshot anywhere in this dialog, or click to pick images"}
            </DropHint>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files) upload(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          <div>
            <FieldLabel>
              Comments{comments.length ? ` (${comments.length})` : ""}
            </FieldLabel>
            <CommentList>
              {comments.map((c) => (
                <CommentEl key={c.id}>
                  <CommentHead>
                    {c.author.fullName}
                    {c.createdAt && ` · ${moment(c.createdAt).fromNow()}`}
                  </CommentHead>
                  <CommentText>{c.text}</CommentText>
                </CommentEl>
              ))}
              <CommentRow>
                <Textarea
                  value={commentText}
                  rows={2}
                  placeholder="Add a comment"
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  isLoading={sending}
                  onClick={sendComment}
                >
                  Send
                </Button>
              </CommentRow>
            </CommentList>
          </div>

          {error && <ErrorText>{error}</ErrorText>}
        </ModalBody>

        {canEdit && (
          <ModalFoot>
            {confirmDelete ? (
              <ActionsRow>
                <ModalSub>Delete this card permanently?</ModalSub>
                <Button
                  variant="danger"
                  size="sm"
                  isLoading={saving}
                  onClick={remove}
                >
                  Delete
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDelete(false)}
                >
                  Keep
                </Button>
              </ActionsRow>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            )}
            <ActionsRow>
              {editing ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={saving}
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={saving}
                    onClick={saveEdits}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditing(true)}
                >
                  Edit
                </Button>
              )}
            </ActionsRow>
          </ModalFoot>
        )}
      </ModalCard>
    </Backdrop>
  );
};

export default CardDetailModal;
